import { useEffect, useMemo, useState } from 'react';
import { useHoneybookAI } from './honeybook-ai-provider';
import { splitLeadingEmoji } from '~/packages/ai-chat/utils';
import { trackError } from '~/services/logger/error.utils';
import { PromptsLibraryItem } from '~/packages/ai-core';

export const usePromptsLibrary = (
    canLoadData: boolean,
    searchQuery: string
) => {
    const ai = useHoneybookAI();

    const [isLoading, setIsLoading] = useState(true);
    const [prompts, setPrompts] = useState<PromptsLibraryItem[]>([]);
    const [categories, setCategories] = useState<string[]>([]);
    const [selectedCategories, setSelectedCategories] = useState<string[]>([]);

    useEffect(() => {
        const getPrompts = async () => {
            try {
                const data = await ai.getAiPromptsLibraryService().getAll();

                setPrompts(data.items);
                setCategories(
                    Array.from(
                        new Set(data.items.map(prompt => prompt.category))
                    )
                );
                setIsLoading(false);
            } catch (error) {
                trackError({
                    error,
                    message: 'Error fetching prompts'
                });
            }
        };

        if (canLoadData) {
            getPrompts();
        }
    }, [ai, canLoadData]);

    const sortedPrompts = useMemo(() => {
        return prompts
            .map(item => ({
                id: item.content,
                content: item.content,
                contentLower: item.content.toLowerCase(),
                contentWithoutEmoji: splitLeadingEmoji(item.content).text,
                category: item.category
            }))
            .sort((a, b) =>
                a.contentWithoutEmoji.localeCompare(b.contentWithoutEmoji)
            );
    }, [prompts]);

    const filteredPrompts = useMemo(() => {
        const hasGroupFilter = selectedCategories.length > 0;

        return sortedPrompts.filter(
            item =>
                (!hasGroupFilter ||
                    selectedCategories.includes(item.category)) &&
                item.contentLower.includes(searchQuery.trim().toLowerCase())
        );
    }, [sortedPrompts, searchQuery, selectedCategories]);

    return {
        isLoading,
        categories,
        selectedCategories,
        setSelectedCategories,
        prompts: filteredPrompts
    };
};
